/**
 * Downscale a chosen image to a small square JPEG data URL, entirely in the
 * browser. Keeps the payload tiny (~256px → ~15-25 KB) so avatars stay well
 * under the server's size cap and the account DB stays lean. No upload of the
 * raw multi-MB file ever happens.
 */
const ACCEPTED = /^image\/(png|jpe?g|webp|gif)$/;
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024; // reject huge originals before decoding

export async function fileToAvatarDataUrl(file: File, size = 256, quality = 0.85): Promise<string> {
  if (!ACCEPTED.test(file.type)) {
    throw new Error('unsupported_type');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error('too_large');
  }
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no_canvas');
    // Cover-crop to a centered square, then scale to `size`.
    const scale = Math.max(size / bitmap.width, size / bitmap.height);
    const w = bitmap.width * scale;
    const h = bitmap.height * scale;
    ctx.drawImage(bitmap, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    bitmap.close();
  }
}

/** First-letter(s) fallback when there's no avatar. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
